import { useEffect, useState } from 'react'
import GoogleMapReact, { MapOptions, Maps } from 'google-map-react'
import { config } from 'data/config'
import MapMarker from 'components/MapMarker'
import classNames from 'classnames'
import { CuratedPropertiesPageQuery } from 'gql/graphql'

type MapProps = {
  id?: string
  center: {
    lat: number
    lng: number
  }
  className?: {
    container?: string
  }
  circleRadius?: number
  height?: string
  options?: (maps: Maps) => MapOptions
  properties?: CuratedPropertiesPageQuery['properties']['data']
}

const Map = ({
  id,
  center,
  circleRadius,
  height = '',
  properties = [],
  options,
  className,
}: MapProps) => {

  const [map, setMap] = useState<any>()
  const [maps, setMaps] = useState<any>()
  const [visibleProperties, setVisibleProperties] = useState<string[]>([])

  const drawCircle = (
    map: any,
    maps: any,
    properties: CuratedPropertiesPageQuery['properties']['data'] = [],
  ) => {
    const options = {
      strokeColor: 'white',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: 'white',
      fillOpacity: 0.3,
      editable: false,
      map,
      center: {
        lat: center.lat,
        lng: center.lng,
      },
      radius: circleRadius,
    }

    if (properties.length) {
      properties.forEach(property => {
        new maps.Circle({
          ...options,
          center: {
            lng: property.position.longitude,
            lat: property.position.latitude,
          },
        })
      })
    } else {
      new maps.Circle(options)
    }
  }

  const createOptions = (maps: Maps) => {
    return {
      fullscreenControl: false,
      mapTypeId: maps.MapTypeId.ROADMAP,
      keyboardShortcuts: false,
      mapTypeControl: true,
      ...options,
    } as MapOptions
  }

  useEffect(() => {
    if (map && maps && properties.length) {
      drawCircle(map, maps, properties.filter(property => !visibleProperties.includes(property.id)))
      setVisibleProperties(properties.map(property => property.id))
    }
  }, [properties, map, maps])

  useEffect(() => {
    if (map && properties.length) {
      const bounds = new window.google.maps.LatLngBounds()
      properties.map(property => {
        bounds.extend({
          lat: property.position.latitude,
          lng: property.position.longitude,
        })
      })
      map.fitBounds(bounds)
    }
  }, [map, properties])

  return (
    <div
      id={id}
      className={classNames(
        'w-full h-[70vh] relative',
        height,
        className?.container,
      )}
    >
      <GoogleMapReact
        bootstrapURLKeys={{ key: config.googleMapsApiKey as string }}
        defaultCenter={center}
        defaultZoom={15}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          setMap(map)
          setMaps(maps)
          if (!properties.length) {
            drawCircle(map, maps)
          }
        }}
        options={createOptions}
      >
        <MapMarker
          lat={center.lat}
          lng={center.lng}
        />
        {properties.map((property) => (
          <MapMarker
            key={property.id}
            lat={property.position?.latitude}
            lng={property.position?.longitude}
            property={property}
          />
        ))}
      </GoogleMapReact>
    </div>
  )
}

export default Map
